import { useState } from 'react';
import { useSearchParams } from 'react-router';
import WorkAnalysis from './widgets/WorkAnalysis';
import WorkAnalysisTable from './widgets/WorkAnalysisTable';
import ICSWidget from './widgets/ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { getIncidentCardTitle } from './USCGPage';

type ViewMode = 'list' | 'table';

const viewModeOptions = [
  { label: 'List View', value: 'list' },
  { label: 'Table View', value: 'table' },
];

const IndependentWorkAnalysisView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-independent-work-analysis-matrix`;

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedTactics, setSelectedTactics] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { exportAsForm } = useIcsFormPdfPreview();
  const handleExportICS234 = () => {
    void exportAsForm({
      formname: 'ICS_234_CG',
      data: {},
      mapSketch: null,
    });
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget"
      contentClassName="content mt-2"
      headerLeft={
        <ICSWidget.HeaderLeft
          iconName="dataset"
          iconColor="primary"
          title={getIncidentCardTitle('workAnalysisMatrix')}
          subtitle=""
        />
      }
      headerRight={
        <ICSWidget.HeaderRight
          icsRoles={['PSC', 'OSC', 'RESL']}
          handleExport={handleExportICS234}
          exportText="Preview ICS-234"
          reviewActionKey="workAnalysis"
        />
      }
    >
      <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
        {canEditCards && (
          <StylishNewButton onClick={() => setAdding(!adding)}>
            <SharedIcon iconName="add" classes="me-1" size="sm" />
            Add Objective
          </StylishNewButton>
        )}
        <StylishNewSearchBar
          onChangeSearchTerm={(term: string) => setSearchTerm(term)}
        />
        <div className="ms-auto">
          <StylishNewToggleButtonGroup
            size="sml"
            data={viewModeOptions}
            selected={viewMode}
            onChange={(value: string) => setViewMode(value as ViewMode)}
          />
        </div>
      </div>
      {viewMode === 'list' ? (
        <WorkAnalysis
          selectedTactics={selectedTactics}
          setSelectedTactics={setSelectedTactics}
          readOnly={!canEditCards}
          standalone
          hideHeader
          searchTerm={searchTerm}
          adding={adding}
          onAddingChange={setAdding}
        />
      ) : (
        <WorkAnalysisTable
          readOnly={!canEditCards}
          hideAddButton
          searchTerm={searchTerm}
          adding={adding}
          onAddingChange={setAdding}
        />
      )}
    </ConditionalCollapsibleCard>
  );
};

export default IndependentWorkAnalysisView;
